import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { useParams } from 'react-router-dom';
import { getScormCourseAnalytics, getScormContentUsers, getScormContentQuestions, getScormUserAttempts, getScormAttemptDetail } from '../../../services/adminService';
import { decodeData } from '../../../utils/encodeAndEncode';

const defaultProfile = '/images/profile/default-profile.png';

function CourseAnalyticsScorm() {
  const { courseId } = useParams();
  const decodedCourseId = decodeData(courseId);

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [view, setView] = useState('overview');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersPagination, setUsersPagination] = useState({ page: 1, limit: 10, total_pages: 0, total_records: 0 });
  const [usersSearch, setUsersSearch] = useState('');
  const [usersStatus, setUsersStatus] = useState('');
  const [questions, setQuestions] = useState([]);
  const [attempts, setAttempts] = useState(null);
  const [attemptDetail, setAttemptDetail] = useState(null);

  useEffect(() => { fetchOverview(); }, [decodedCourseId]);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const res = await getScormCourseAnalytics(decodedCourseId);
      if (res.success) { setSummary(res.data.summary); setItems(res.data.items || []); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchUsers = async (scormContentId, page = 1) => {
    try {
      setLoading(true);
      const res = await getScormContentUsers(scormContentId, { page, limit: 10, status: usersStatus, search: usersSearch });
      if (res.success) { setUsers(res.data.users || []); setUsersPagination(res.data.pagination); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchQuestions = async (scormContentId) => {
    try {
      setLoading(true);
      const res = await getScormContentQuestions(scormContentId);
      if (res.success) setQuestions(res.data.questions || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchAttempts = async (userId, scormContentId) => {
    try {
      setLoading(true);
      const res = await getScormUserAttempts(userId, scormContentId);
      if (res.success) setAttempts(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchAttemptDetail = async (userId, scormContentId, attemptNumber) => {
    try {
      setLoading(true);
      const res = await getScormAttemptDetail(userId, scormContentId, attemptNumber);
      if (res.success) setAttemptDetail(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleItemClick = (item) => { setSelectedItem(item); setView('users'); fetchUsers(item.scorm_content_id); };
  const handleQuestionsClick = (item) => { setSelectedItem(item); setView('questions'); fetchQuestions(item.scorm_content_id); };
  const handleUserClick = (user) => { setSelectedUser(user); setView('attempts'); fetchAttempts(user.user_id, selectedItem.scorm_content_id); };
  const handleAttemptClick = (attempt) => { setView('detail'); fetchAttemptDetail(selectedUser.user_id, selectedItem.scorm_content_id, attempt.attempt_number); };

  const handleBack = () => {
    if (view === 'detail') { setView('attempts'); setAttemptDetail(null); }
    else if (view === 'attempts') { setView('users'); setAttempts(null); }
    else if (view === 'users' || view === 'questions') { setView('overview'); setSelectedItem(null); }
    else { setView('overview'); }
  };

  const StatusBadge = ({ status }) => {
    const s = String(status).toLowerCase();
    const config = s === 'passed' || s === 'completed'
      ? 'bg-green-100 text-green-700' : s === 'failed'
      ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700';
    return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${config} capitalize`}>{status || 'N/A'}</span>;
  };

  const ScoreDisplay = ({ score }) => {
    if (score === null || score === undefined || score === '') return <span className="text-gray-400">—</span>;
    const num = parseFloat(score);
    if (isNaN(num)) return <span className="text-gray-400">—</span>;
    const color = num >= 80 ? 'text-green-600' : num >= 60 ? 'text-amber-600' : 'text-red-600';
    return <span className={`font-semibold ${color}`}>{Math.round(num)}%</span>;
  };

  // Format SCORM time (0000:00:00.00 → readable or hide if zero)
  const formatTime = (timeStr) => {
    if (!timeStr || timeStr === '0000:00:00.00' || timeStr === '0000:00:00' || timeStr === '00:00:00' || timeStr === '') return '—';
    // Try to parse HH:MM:SS or HHHH:MM:SS.ss format
    const parts = timeStr.replace(/\..*$/, '').split(':');
    if (parts.length >= 3) {
      const hrs = parseInt(parts[0]) || 0;
      const mins = parseInt(parts[1]) || 0;
      const secs = parseInt(parts[2]) || 0;
      if (hrs === 0 && mins === 0 && secs === 0) return '—';
      if (hrs > 0) return `${hrs}h ${mins}m`;
      if (mins > 0) return `${mins}m ${secs}s`;
      return `${secs}s`;
    }
    return timeStr;
  };

  const Spinner = () => (
    <div className="flex justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-[#7083FF]" />
    </div>
  );

  if (loading && view === 'overview' && !summary) return <Spinner />;

  // ─── OVERVIEW ───────────────────────────────────────────────────────
  if (view === 'overview') {
    if (!items.length) {
      return (
        <div className="text-center py-12 text-gray-500">
          <Icon icon="fluent:archive-24-regular" width={48} height={48} className="mx-auto mb-3 text-gray-300" />
          <p className="font-medium text-lg">No SCORM content in this course</p>
          <p className="text-sm mt-1">Add SCORM packages to modules to see analytics here.</p>
        </div>
      );
    }

    return (
      <div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
          <div className="p-4 rounded-xl border border-gray-100 bg-gradient-to-br from-white to-gray-50">
            <p className="text-xs text-gray-500 font-medium mb-1">SCORM Lessons</p>
            <p className="text-2xl font-bold text-gray-900">{summary?.total_scorm_items || 0}</p>
          </div>
          <div className="p-4 rounded-xl border border-gray-100 bg-gradient-to-br from-white to-gray-50">
            <p className="text-xs text-gray-500 font-medium mb-1">Users Attempted</p>
            <p className="text-2xl font-bold text-gray-900">{summary?.total_users_attempted || 0}</p>
          </div>
          <div className="p-4 rounded-xl border border-green-100 bg-gradient-to-br from-white to-green-50">
            <p className="text-xs text-gray-500 font-medium mb-1">Pass Rate</p>
            <p className="text-2xl font-bold text-green-600">{summary?.overall_pass_rate || 0}%</p>
          </div>
          <div className="p-4 rounded-xl border border-indigo-100 bg-gradient-to-br from-white to-indigo-50">
            <p className="text-xs text-gray-500 font-medium mb-1">Avg Score</p>
            <p className="text-2xl font-bold text-[#7083FF]">{summary?.overall_avg_score || 0}%</p>
          </div>
          <div className="p-4 rounded-xl border border-gray-100 bg-gradient-to-br from-white to-gray-50">
            <p className="text-xs text-gray-500 font-medium mb-1">Avg Retries to Pass</p>
            <p className="text-2xl font-bold text-gray-900">{summary?.avg_attempts_to_pass || 1}</p>
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-gray-100">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">SCORM Lesson</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">Users</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">Attempts</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">Pass Rate</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">Avg Score</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">Time</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => (
                <tr key={item.scorm_content_id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{item.title}</div>
                    <div className="text-xs text-gray-500 mt-0.5">SCORM {item.version}</div>
                  </td>
                  <td className="px-4 py-3 text-center text-gray-800">{item.unique_users}</td>
                  <td className="px-4 py-3 text-center text-gray-800">{item.total_attempts}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`font-semibold ${item.pass_rate >= 70 ? 'text-green-600' : item.pass_rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                      {item.pass_rate}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center"><ScoreDisplay score={item.avg_score} /></td>
                  <td className="px-4 py-3 text-center text-gray-500">{item.avg_time || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => handleItemClick(item)}
                      className="px-3 py-1.5 text-xs font-medium bg-[#7083FF] text-white rounded-lg hover:bg-[#5c6ef0] transition-colors">
                      View Users
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ─── USERS VIEW ─────────────────────────────────────────────────────
  if (view === 'users') {
    return (
      <div>
        <button onClick={handleBack} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#7083FF] mb-4 transition-colors">
          <Icon icon="mdi:arrow-left" width={18} /> Back to Overview
        </button>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{selectedItem?.title}</h3>
            <p className="text-sm text-gray-500">User performance breakdown</p>
          </div>
          <button onClick={() => handleQuestionsClick(selectedItem)}
            className="px-3 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 border border-amber-200 transition-colors">
            <Icon icon="fluent:quiz-new-24-regular" width={14} className="inline mr-1" />
            Question Analysis
          </button>
        </div>

        <div className="flex gap-3 mb-4">
          <input type="search" placeholder="Search users..." value={usersSearch}
            onChange={(e) => setUsersSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') fetchUsers(selectedItem.scorm_content_id); }}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-[#7083FF]/20 focus:border-[#7083FF]" />
          <select value={usersStatus} onChange={(e) => { setUsersStatus(e.target.value); setTimeout(() => fetchUsers(selectedItem.scorm_content_id), 0); }}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#7083FF]/20">
            <option value="">All Status</option>
            <option value="passed">Passed</option>
            <option value="failed">Failed</option>
            <option value="incomplete">In Progress</option>
          </select>
        </div>

        {loading ? <Spinner /> : (
          <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">User</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Score</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Status</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Attempts</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Time</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Last Activity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-8 text-gray-500">No users found</td></tr>
                ) : users.map((user) => (
                  <tr key={user.user_id} className="hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => handleUserClick(user)}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img src={user.profile_pic_url || defaultProfile} alt="" className="w-9 h-9 rounded-full object-cover border border-gray-100" onError={(e) => { e.target.src = defaultProfile; }} />
                        <div><p className="font-medium text-gray-900">{user.user_name}</p><p className="text-xs text-gray-500">{user.email}</p></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center"><ScoreDisplay score={user.score_percentage || user.score} /></td>
                    <td className="px-4 py-3 text-center"><StatusBadge status={user.lesson_status} /></td>
                    <td className="px-4 py-3 text-center font-medium text-gray-800">{user.total_attempts || 1}</td>
                    <td className="px-4 py-3 text-center text-gray-500">{formatTime(user.total_time)}</td>
                    <td className="px-4 py-3 text-center text-gray-500 text-xs">{user.last_attempt_date ? new Date(user.last_attempt_date).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {usersPagination.total_pages > 1 && (
          <div className="flex justify-center gap-3 mt-4">
            <button disabled={usersPagination.current_page <= 1} onClick={() => fetchUsers(selectedItem.scorm_content_id, usersPagination.current_page - 1)}
              className="px-4 py-2 text-sm bg-[#7083FF] text-white rounded-lg disabled:opacity-50">Prev</button>
            <span className="py-2 text-sm text-gray-600">Page {usersPagination.current_page} of {usersPagination.total_pages}</span>
            <button disabled={usersPagination.current_page >= usersPagination.total_pages} onClick={() => fetchUsers(selectedItem.scorm_content_id, usersPagination.current_page + 1)}
              className="px-4 py-2 text-sm bg-[#7083FF] text-white rounded-lg disabled:opacity-50">Next</button>
          </div>
        )}
      </div>
    );
  }

  // ─── QUESTIONS VIEW ─────────────────────────────────────────────────
  if (view === 'questions') {
    return (
      <div>
        <button onClick={handleBack} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#7083FF] mb-4 transition-colors">
          <Icon icon="mdi:arrow-left" width={18} /> Back
        </button>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{selectedItem?.title} — Question Analysis</h3>

        {loading ? <Spinner /> : questions.length === 0 ? (
          <div className="rounded-2xl border border-amber-100 bg-amber-50/50 p-8 text-center">
            <Icon icon="fluent:info-24-regular" width={40} height={40} className="mx-auto mb-3 text-amber-500" />
            <p className="font-semibold text-gray-900 text-lg mb-2">Question-level data not available</p>
            <p className="text-sm text-gray-600 max-w-md mx-auto mb-4">
              This SCORM package does not report individual question responses. Only overall score and pass/fail status are available.
            </p>
            <div className="text-xs text-gray-500 bg-white rounded-xl p-4 max-w-sm mx-auto border border-gray-100">
              <p className="font-semibold text-gray-700 mb-2">To enable question-level analytics:</p>
              <ul className="text-left space-y-1.5">
                <li className="flex items-start gap-2"><Icon icon="mdi:check" width={14} className="text-green-500 mt-0.5 shrink-0" /> In your authoring tool, enable "Report interactions to LMS"</li>
                <li className="flex items-start gap-2"><Icon icon="mdi:check" width={14} className="text-green-500 mt-0.5 shrink-0" /> Re-export and re-upload the SCORM package</li>
                <li className="flex items-start gap-2"><Icon icon="mdi:check" width={14} className="text-green-500 mt-0.5 shrink-0" /> Future user attempts will show per-question data</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">#</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Question</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Type</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">% Correct</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Responses</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Common Wrong</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Correct Answer</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {questions.map((q, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-500 font-medium">{q.interaction_index + 1}</td>
                    <td className="px-4 py-3 text-gray-900 max-w-xs">{q.description}</td>
                    <td className="px-4 py-3 text-center text-gray-500 capitalize">{q.type || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`font-semibold ${q.correct_rate >= 70 ? 'text-green-600' : q.correct_rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                        {q.correct_rate}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-700">{q.total_responses}</td>
                    <td className="px-4 py-3 text-center text-red-600 font-medium">{q.most_common_wrong_answer || '—'}</td>
                    <td className="px-4 py-3 text-center text-green-600 font-medium">{q.correct_response || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // ─── ATTEMPTS VIEW ──────────────────────────────────────────────────
  if (view === 'attempts') {
    return (
      <div>
        <button onClick={handleBack} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#7083FF] mb-4 transition-colors">
          <Icon icon="mdi:arrow-left" width={18} /> Back to Users
        </button>

        <div className="flex items-center gap-4 mb-5">
          <img src={attempts?.profile_pic_url || selectedUser?.profile_pic_url || defaultProfile} alt="" className="w-12 h-12 rounded-full border border-gray-100" onError={(e) => { e.target.src = defaultProfile; }} />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{attempts?.user_name || selectedUser?.user_name || 'User'}</h3>
            <p className="text-sm text-gray-500">{selectedItem?.title}</p>
          </div>
        </div>

        {loading ? <Spinner /> : (
          <>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="p-4 rounded-xl border border-gray-100 bg-gradient-to-br from-white to-gray-50">
                <p className="text-xs text-gray-500 mb-1">Total Attempts</p>
                <p className="text-2xl font-bold text-gray-900">{attempts?.total_attempts || 0}</p>
              </div>
              <div className="p-4 rounded-xl border border-green-100 bg-gradient-to-br from-white to-green-50">
                <p className="text-xs text-gray-500 mb-1">Best Score</p>
                <p className="text-2xl font-bold text-green-600">{attempts?.best_score || 0}%</p>
              </div>
              <div className="p-4 rounded-xl border border-gray-100 bg-gradient-to-br from-white to-gray-50">
                <p className="text-xs text-gray-500 mb-1">Latest Status</p>
                <div className="mt-1"><StatusBadge status={attempts?.latest_status || 'N/A'} /></div>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-100">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-center font-semibold text-gray-600">Attempt</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-600">Date</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-600">Score</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-600">Status</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-600">Questions</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-600">Time</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-600">Detail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(attempts?.attempts || []).map((a) => (
                    <tr key={a.attempt_number} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-center font-semibold text-gray-800">#{a.attempt_number}</td>
                      <td className="px-4 py-3 text-center text-gray-500 text-xs">{a.started_at ? new Date(a.started_at).toLocaleDateString() : '—'}</td>
                      <td className="px-4 py-3 text-center"><ScoreDisplay score={a.score_percentage || a.score_raw} /></td>
                      <td className="px-4 py-3 text-center"><StatusBadge status={a.lesson_status} /></td>
                      <td className="px-4 py-3 text-center">
                        {a.total_questions > 0 ? (
                          <span><span className="text-green-600 font-medium">{a.correct_answers}</span>/<span className="text-gray-600">{a.total_questions}</span></span>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-500">{formatTime(a.session_time || a.total_time)}</td>
                      <td className="px-4 py-3 text-center">
                        {a.total_questions > 0 ? (
                          <button onClick={() => handleAttemptClick(a)}
                            className="px-3 py-1.5 text-xs font-medium bg-[#7083FF]/10 text-[#7083FF] rounded-lg hover:bg-[#7083FF]/20 transition-colors">
                            View
                          </button>
                        ) : <span className="text-gray-400 text-xs">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    );
  }

  // ─── ATTEMPT DETAIL VIEW ────────────────────────────────────────────
  if (view === 'detail') {
    return (
      <div>
        <button onClick={handleBack} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#7083FF] mb-4 transition-colors">
          <Icon icon="mdi:arrow-left" width={18} /> Back to Attempts
        </button>
        <div className="flex items-center gap-4 mb-5">
          <h3 className="text-lg font-semibold text-gray-900">Attempt #{attemptDetail?.attempt_number}</h3>
          <StatusBadge status={attemptDetail?.lesson_status || ''} />
          <span className="text-sm text-gray-500">
            <ScoreDisplay score={attemptDetail?.score_percentage} /> · {attemptDetail?.correct_answers || 0}/{attemptDetail?.total_questions || 0} correct
          </span>
        </div>

        {loading ? <Spinner /> : !attemptDetail?.interactions?.length ? (
          <div className="rounded-2xl border border-amber-100 bg-amber-50/50 p-6 text-center">
            <Icon icon="fluent:info-24-regular" width={32} className="mx-auto mb-2 text-amber-500" />
            <p className="font-medium text-gray-900">No question detail for this attempt</p>
            <p className="text-sm text-gray-500 mt-1">The SCORM package did not report individual question responses.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">#</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Question</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">User Answer</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Correct Answer</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Result</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {attemptDetail.interactions.map((q, idx) => {
                  const isCorrect = q.result?.toLowerCase() === 'correct';
                  return (
                    <tr key={idx} className={isCorrect ? '' : 'bg-red-50/40'}>
                      <td className="px-4 py-3 text-gray-500 font-medium">{q.interaction_index + 1}</td>
                      <td className="px-4 py-3 text-gray-900">{q.description || q.interaction_id || `Q${q.interaction_index + 1}`}</td>
                      <td className={`px-4 py-3 text-center font-medium ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>{q.learner_response || '—'}</td>
                      <td className="px-4 py-3 text-center text-green-700 font-medium">{q.correct_response || '—'}</td>
                      <td className="px-4 py-3 text-center">
                        {isCorrect
                          ? <Icon icon="mdi:check-circle" width={22} className="text-green-500 inline" />
                          : <Icon icon="mdi:close-circle" width={22} className="text-red-500 inline" />}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-500 text-xs">{q.latency || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return null;
}

export default CourseAnalyticsScorm;
